<template>
    <div>
        <div v-if="step !== 'success'">
            <div v-if="lookupError" class="hero__error">{{ lookupError }}</div>
            <RegisterCodeForm ref="codeFormRef" :loading="lookingUp" @submit-code="onSubmitCode" />
        </div>

        <div v-else-if="step === 'success'" class="hero__intro">
            <div v-html="successHtml"></div>
            <div class="hero__thank-link"><a href="#" @click.prevent="resetToCodeEntry">Registrer flere turkoder &raquo;</a></div>
            <div class="hero__thank-link"><a href="/turer">Finn flere turmål &raquo;</a></div>
        </div>

        <RegisterConfirmModal
            :open="step === 'confirm'"
            :route="route"
            :date-bounds="dateBounds"
            :family-members="familyMembers"
            :person-id="personId"
            :submitting="submitting"
            :error="confirmError"
            @cancel="onCancel"
            @confirm="onConfirm"
        />
    </div>
</template>

<script setup>
import { ref } from 'vue';
import RegisterCodeForm from './RegisterCodeForm.vue';
import RegisterConfirmModal from './RegisterConfirmModal.vue';

const props = defineProps({
    csrfToken: { type: String, required: true },
    personId: { type: Number, required: true },
    familyMembers: { type: Array, default: () => [] },
});

// 'code' (entering a trip code) -> 'confirm' (the modal: date + companions)
// -> 'success' (after_registration_text), or back to 'code' on cancel/error.
const step = ref('code');
const route = ref(null);
const dateBounds = ref(null);
const lookupError = ref(null);
const confirmError = ref(null);
const lookingUp = ref(false);
const submitting = ref(false);
const successHtml = ref('');
const codeFormRef = ref(null);

function postJson(url, body) {
    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': props.csrfToken,
        },
        body: JSON.stringify(body),
    });
}

async function onSubmitCode(code) {
    lookupError.value = null;
    lookingUp.value = true;
    try {
        const response = await postJson('/xhr/frontend/routes/code-lookup', { code: code.split('') });
        const data = await response.json();

        if (!response.ok) {
            lookupError.value = data.error || '! Ugyldig kode !';
            return;
        }

        route.value = data.route;
        dateBounds.value = data.date;
        confirmError.value = null;
        step.value = 'confirm';
    } finally {
        lookingUp.value = false;
    }
}

async function onConfirm({ date, members }) {
    confirmError.value = null;
    submitting.value = true;
    try {
        const response = await postJson('/xhr/frontend/users/registrations', {
            codeword: route.value.codeword,
            members,
            date,
        });
        const data = await response.json();

        if (!response.ok) {
            confirmError.value = data.error || 'Noe gikk galt, prøv igjen.';
            return;
        }

        successHtml.value = data.after_registration_text;
        step.value = 'success';
        // MyPageCard.vue (a separately-mounted Vue app) listens for this to
        // refresh the just-registered member's stats/registration lists.
        window.dispatchEvent(new CustomEvent('mypage:registration-added'));
    } finally {
        submitting.value = false;
    }
}

function onCancel() {
    step.value = 'code';
    route.value = null;
    dateBounds.value = null;
    confirmError.value = null;
    codeFormRef.value?.clear();
}

function resetToCodeEntry() {
    step.value = 'code';
    successHtml.value = '';
}
</script>
